import { FC, memo, useMemo, useState } from 'react';
import { TextField, Grid, Box, Checkbox, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { UserSimple } from 'src/types/userSimple';
import TypeAhead from 'src/components/controls/TypeAhead';
import moment from 'moment';
import useCrm from 'src/hooks/useCrm';
import { ActivityFormState } from 'src/types/activityFormState';
import { getUniqueUsersByUsername } from 'src/utils/userHelpers';

interface Props {
  formState: ActivityFormState;
  onAssignedToChange: (assignedTo: number | null) => void;
  onDueDateChange: (dueDate: string | null) => void;
  onAddToOutlookChange: (addToOutlook: boolean) => void;
}

function dueAfterNow(d: string) {
  const dueDate = moment(d);
  if (dueDate.isSameOrAfter(moment(), 'day')) {
    return dueDate;
  }
}

const ActivityTaskFields: FC<Props> = ({ formState, onAssignedToChange, onDueDateChange, onAddToOutlookChange }) => {
  const [filteredSfaUsers, setFilteredSfaUsers] = useState<UserSimple[] | null>(null);
  const { allSFAUsers } = useCrm();
  const sfaUsers = useMemo(() => getUniqueUsersByUsername(allSFAUsers, formState.formActivity.assignedTo), [allSFAUsers, formState.formActivity.assignedTo]);
  const assignedToOptions = filteredSfaUsers ?? sfaUsers;

  function onUserFilterChange(value: string | null) {
    if (value === null) {
      setFilteredSfaUsers(null);
    } else {
      setFilteredSfaUsers(sfaUsers.filter((x) => x.username.toUpperCase().includes(value.toUpperCase())));
    }
  }

  const defaultAssignedTo = sfaUsers.find((x) => x.companyUserId === formState.formActivity.assignedTo);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Box>
          {sfaUsers.length > 0 && (
            <TypeAhead
              defaultValue={defaultAssignedTo}
              sx={{ width: '100%', m: 0 }}
              data={assignedToOptions}
              minFilterLength={1}
              onChange={(userSelected: UserSimple | null) => onAssignedToChange(userSelected?.companyUserId ?? null)}
              onFilterChange={onUserFilterChange}
              getOptionLabel={(option) => option.username}
              getOptionKey={(option: UserSimple) => option.companyUserId}
              placeholder={'Assign To'}
              title="Assign this task to a user"
            />
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ minWidth: '200px' }}>
            <DatePicker
              views={['year', 'month', 'day']}
              label="Due Date"
              value={formState.formActivity.dueDate ? new Date(formState.formActivity.dueDate) : null}
              onChange={(dueDate) => {
                onDueDateChange(dueDate ? new Date(dueDate).toISOString() : null);
              }}
            />
          </Box>
          {dueAfterNow(formState.formActivity.dueDate) && (
            <FormControlLabel control={<Checkbox onChange={(e) => onAddToOutlookChange(e.target.checked)} />} label="Add to calendar" sx={{ minWidth: '200px', pl: 3 }} />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default memo(ActivityTaskFields);
